import {
  Box,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Tooltip,
} from "@mui/material";
import React from "react";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useReferenceData } from "../../../hooks/useReferenceData";
import { FOUR_R_SOIL_TEST_CREATOR } from "../../../Assets/contextFactories_V0";
import CropYearDatePicker from "../CropYearDatePicker";

const SOIL_TEST_METHODS = {
  N: [{ value: "Nitrate-N", label: "Nitrate-N (NO3-N)" }],
  P: [
    { value: "Olsen", label: "Olsen" },
    { value: "Bray", label: "Bray" },
  ],
  K: [
    {
      value: "Ammonium Acetate",
      label: "Ammonium acetate extract",
    },
  ],
  S: [
    {
      value: "Calcium Phosphate",
      label: "Calcium phosphate extract",
    },
  ],
  M: [{ value: "DTPA", label: "DTPA micronutrient" }],
  OM: [{ value: "Loss on Ignition", label: "Loss on ignition" }],
};

const SoilTest = ({
  cropYear,
  data,
  index,
  error,
  fourRAssessment,
  setFourRAssessment,
  onAssessmentChange,
}) => {
  // soil_test_nutrients is a superset of fertilizer_nutrients that also
  // includes "OM" (Organic Matter %), used to source Holos's soilOrganicMatterPercent.
  const { referenceData } = useReferenceData(["soil_test_nutrients"]);
  const soilTestNutrientTypes = referenceData.soil_test_nutrients || [];
  const rowLabel = `Soil test #${index + 1}`;
  const recommendedMethods = SOIL_TEST_METHODS[data.nutrient] || [];
  const methodOptions =
    data.test &&
    !recommendedMethods.some((method) => method.value === data.test)
      ? [
          {
            value: data.test,
            label: `${data.test} (previous entry)`,
          },
          ...recommendedMethods,
        ]
      : recommendedMethods;
  const fieldIsMissing = (field) =>
    Boolean(error[field]) &&
    (data[field] === "" || data[field] === null || data[field] === undefined);
  const resultIsInvalid =
    data.result === "" ||
    !Number.isFinite(Number(data.result)) ||
    Number(data.result) < 0 ||
    (data.nutrient === "OM" && Number(data.result) > 100);

  function handleSoilTestUpdate(property, value) {
    onAssessmentChange();
    setFourRAssessment((prevState) => ({
      ...prevState,
      soil_tests: (prevState.soil_tests || []).map((test, rowIndex) =>
        rowIndex === index
          ? {
              ...test,
              [property]: value,
              ...(property === "nutrient"
                ? {
                    test: SOIL_TEST_METHODS[value]?.[0]?.value || "",
                    units:
                      value === "OM"
                        ? "%"
                        : test.units === "%"
                        ? ""
                        : test.units,
                  }
                : {}),
            }
          : test
      ),
    }));
  }

  function handleSoilTestDelete() {
    onAssessmentChange();
    setFourRAssessment((prevState) => {
      const soilTests = prevState.soil_tests || [];

      return {
        ...prevState,
        soil_tests:
          soilTests.length <= 1
            ? [FOUR_R_SOIL_TEST_CREATOR()]
            : soilTests.filter((_, rowIndex) => rowIndex !== index),
      };
    });
  }

  function handleMoveUpDown(direction) {
    const soilTests = fourRAssessment.soil_tests || [];
    if (soilTests.length <= 1) return;
    if (direction < 0 && index === 0) return;
    if (direction > 0 && index === soilTests.length - 1) return;

    onAssessmentChange();
    setFourRAssessment((prevState) => {
      const nextTests = [...(prevState.soil_tests || [])];
      const tmp = nextTests[index];
      nextTests[index] = nextTests[index + direction];
      nextTests[index + direction] = tmp;

      return {
        ...prevState,
        soil_tests: nextTests,
      };
    });
  }

  return (
    <Box
      id={`soil-test-row-${index}`}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(6, minmax(0, 1fr))",
        },
        gap: 1.5,
        padding: { xs: 1.5, sm: 2 },
        "& > *": {
          minWidth: 0,
        },
      }}
    >
      <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>Soil test {index + 1}</Box>
          <Box component="span" sx={{ ml: 1, fontSize: 13, color: "text.secondary" }}>
            {data.nutrient ? `${data.nutrient}${data.date ? `, ${data.date}` : ""}` : "New record"}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          <Tooltip title="Move test up">
            <span>
              <IconButton size="small" aria-label={`Move ${rowLabel} up`} disabled={index === 0} onClick={() => handleMoveUpDown(-1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move test down">
            <span>
              <IconButton
                size="small"
                aria-label={`Move ${rowLabel} down`}
                disabled={index === (fourRAssessment.soil_tests || []).length - 1}
                onClick={() => handleMoveUpDown(1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete test">
            <IconButton size="small" aria-label={`Delete ${rowLabel}`} onClick={handleSoilTestDelete}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ gridColumn: { lg: "span 2" } }}>
        <FormControl fullWidth size="small">
          <InputLabel>Nutrient</InputLabel>
          <Select
            value={data.nutrient}
            onChange={(e) => handleSoilTestUpdate("nutrient", e.target.value)}
            label="Nutrient"
            error={fieldIsMissing("nutrient")}
            inputProps={{ "aria-label": `${rowLabel} nutrient` }}
          >
            {soilTestNutrientTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Test</InputLabel>
          <Select
            value={data.test}
            onChange={(e) => handleSoilTestUpdate("test", e.target.value)}
            label="Test"
            error={fieldIsMissing("test")}
            inputProps={{ "aria-label": `${rowLabel} test` }}
            disabled={!data.nutrient}
          >
            {methodOptions.map((method) => (
              <MenuItem key={method.value} value={method.value}>
                {method.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <CropYearDatePicker
          cropYear={cropYear}
          label="Date"
          value={data.date}
          onChange={(value) => handleSoilTestUpdate("date", value)}
          legacyFormat
          monthOnly
          textFieldProps={{
            fullWidth: true,
            error: fieldIsMissing("date"),
            inputProps: { "aria-label": `${rowLabel} date` },
          }}
        />
      </Box>

      <Box>
        <TextField
          type="number"
          size="small"
          label="Result"
          value={data.result}
          onChange={(e) => handleSoilTestUpdate("result", e.target.value)}
          fullWidth
          error={Boolean(error.result) && resultIsInvalid}
          helperText={Boolean(error.result) && resultIsInvalid ? (data.nutrient === "OM" ? "Use 0 to 100%" : "Enter zero or greater") : ""}
          inputProps={{
            min: 0,
            ...(data.nutrient === "OM" ? { max: 100 } : {}),
            step: "any",
            "aria-label": `${rowLabel} result`,
          }}
        />
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Units</InputLabel>
          <Select
            value={data.units}
            onChange={(e) => handleSoilTestUpdate("units", e.target.value)}
            label="Units"
            error={fieldIsMissing("units")}
            inputProps={{ "aria-label": `${rowLabel} units` }}
            disabled={data.nutrient === "OM"}
          >
            <MenuItem value="%">%</MenuItem>
            <MenuItem value="Lbs/Acre">lbs/acre</MenuItem>
            <MenuItem value="ppm">ppm</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth size="small">
          <InputLabel>Frequency</InputLabel>
          <Select
            value={data.frequency}
            onChange={(e) => handleSoilTestUpdate("frequency", e.target.value)}
            label="Frequency"
            error={fieldIsMissing("frequency")}
            inputProps={{ "aria-label": `${rowLabel} frequency` }}
          >
            <MenuItem value="Yearly">Yearly</MenuItem>
            <MenuItem value="Every 5 years">Every 5 years</MenuItem>
            <MenuItem value="Every 4 years">Every 4 years</MenuItem>
            <MenuItem value="Every 3 years">Every 3 years</MenuItem>
            <MenuItem value="Every 2 years">Every 2 years</MenuItem>
            <MenuItem value="Two out of 3 years">Two out of 3 years</MenuItem>
            <MenuItem value="Two out of 5 years">Two out of 5 years</MenuItem>
          </Select>
        </FormControl>
      </Box>

    </Box>
  );
};

export default SoilTest;
